import { formatDate as formatDisplayDate } from '@/lib/formatDate';
import React, { useState, useEffect, useRef } from 'react';
import {
  X, Loader2, FileText, Receipt, Printer, ChevronDown, ChevronUp,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { usePermission } from '@/hooks/usePermission';
import {
  getPatient, getPatientInvoices, getPatientPrescriptions,
} from '@/lib/api';
import { displayAge } from '@/lib/patientAge';

interface Props {
  patientId: string;
  onClose: () => void;
  onCollectPayment: (invoice: any) => void;
  onPrintPrescription: (rx: any) => void;
  onPrintInvoice: (inv: any) => void;
}

function formatDate(dateStr: string) {
  return formatDisplayDate(dateStr);
}

function PrescriptionRow({ prescriptions, onPrint }: { prescriptions: any[]; onPrint: (rx: any) => void }) {
  const [expanded, setExpanded] = useState(false);
  const primary = prescriptions[0];
  const multiple = prescriptions.length > 1;

  return (
    <div className="mb-1.5">
      <div className="flex items-center justify-between p-2.5 rounded-lg border border-border bg-background">
        <div className="flex items-center gap-2.5 min-w-0">
          <FileText className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-medium">Prescription</span>
              {multiple && (
                <span className="text-2xs bg-muted text-muted-foreground px-1.5 py-0.5 rounded-full">×{prescriptions.length}</span>
              )}
            </div>
            <p className="text-2xs text-muted-foreground truncate">
              {primary.doctor_name ? `Dr. ${primary.doctor_name}` : 'Doctor'}
              {primary.diagnosis && ` · ${primary.diagnosis}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {multiple && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="p-1 rounded hover:bg-muted text-muted-foreground"
            >
              {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
          )}
          <button
            onClick={() => onPrint(primary)}
            className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
            title="Print"
          >
            <Printer className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {multiple && expanded && (
        <div className="ml-3 mt-1 border-l-2 border-border pl-3 space-y-1">
          {prescriptions.map((rx, i) => (
            <div key={rx.id} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-muted/50">
              <div>
                <p className="text-2xs font-medium">Prescription {i + 1}</p>
                <p className="text-2xs text-muted-foreground">
                  {rx.diagnosis || (rx.medicines?.length ? `${rx.medicines.length} medicines` : 'No details')}
                </p>
              </div>
              <button onClick={() => onPrint(rx)} className="p-1 rounded hover:bg-muted text-muted-foreground">
                <Printer className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PatientDrawer({
  patientId,
  onClose,
  onCollectPayment,
  onPrintPrescription,
  onPrintInvoice,
}: Props) {
  const { can } = usePermission();
  const [patient, setPatient] = useState<any>(null);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [prescriptions, setPrescriptions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const drawerRef = useRef<HTMLDivElement>(null);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  useEffect(() => {
    if (!patientId) return;
    setLoading(true);
    Promise.all([
      getPatient(patientId),
      can('billing:read') ? getPatientInvoices(patientId) : Promise.resolve([]),
      can('prescriptions:read') ? getPatientPrescriptions(patientId) : Promise.resolve([]),
    ]).then(([p, inv, rx]) => {
      setPatient(p);
      setInvoices(Array.isArray(inv) ? inv : []);
      setPrescriptions(Array.isArray(rx) ? rx : []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [patientId]);

  // Group all visits by date
  const groupedVisits = React.useMemo(() => {
    const map: Record<string, { date: string; prescriptions: any[]; invoices: any[] }> = {};
    prescriptions.forEach(rx => {
      const date = (rx.date || rx.created_at || '').split('T')[0];
      if (!map[date]) map[date] = { date, prescriptions: [], invoices: [] };
      map[date].prescriptions.push(rx);
    });
    invoices.forEach(inv => {
      const date = (inv.invoice_date || inv.created_at || '').split('T')[0];
      if (!map[date]) map[date] = { date, prescriptions: [], invoices: [] };
      map[date].invoices.push(inv);
    });
    return Object.values(map).sort((a, b) => b.date.localeCompare(a.date));
  }, [prescriptions, invoices]);

  const outstanding = invoices
    .filter(inv => inv.payment_status === 'unpaid' || inv.payment_status === 'partial')
    .reduce((sum, inv) => sum + (Number(inv.total_amount) - Number(inv.paid_amount || 0)), 0);

  const totalBilled = invoices.reduce((sum, inv) => sum + Number(inv.total_amount || 0), 0);

  const memberSince = React.useMemo(() => {
    if (groupedVisits.length === 0) return null;
    const oldest = groupedVisits[groupedVisits.length - 1].date;
    return formatDate(oldest);
  }, [groupedVisits]);

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/20"
        onClick={onClose}
      />

      {/* Drawer panel */}
      <div
        ref={drawerRef}
        className="kb-drawer fixed top-0 right-0 z-50 h-full w-[480px] max-w-[90vw] bg-background border-l border-border shadow-2xl flex flex-col"
        style={{ top: 'var(--topbar-height, 60px)', height: 'calc(100dvh - var(--topbar-height, 60px))' }}
      >
        {/* Header */}
        <div className="flex-none border-b border-border px-4 py-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              {loading ? (
                <div className="h-5 w-32 bg-muted animate-pulse rounded" />
              ) : (
                <>
                  <h2 className="text-sm font-bold capitalize truncate">{patient?.name}</h2>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {[
                      displayAge(patient?.age, patient?.date_of_birth),
                      patient?.gender,
                      patient?.phone,
                      patient?.mrn && `MRN: ${patient.mrn}`,
                    ].filter(Boolean).join(' · ')}
                  </p>
                </>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground flex-shrink-0"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Stats strip */}
          {!loading && (
            <div className="flex items-center gap-4 mt-3">
              <div>
                <p className="text-xs font-bold">{groupedVisits.length}</p>
                <p className="text-2xs text-muted-foreground">Visits</p>
              </div>
              {memberSince && (
                <div>
                  <p className="text-xs font-bold">{memberSince}</p>
                  <p className="text-2xs text-muted-foreground">First visit</p>
                </div>
              )}
              {totalBilled > 0 && (
                <div>
                  <p className="text-xs font-bold">₹{totalBilled.toLocaleString('en-IN')}</p>
                  <p className="text-2xs text-muted-foreground">Total billed</p>
                </div>
              )}
              {outstanding > 0 && (
                <div>
                  <p className="text-xs font-bold text-destructive">₹{outstanding.toLocaleString('en-IN')}</p>
                  <p className="text-2xs text-muted-foreground">Outstanding</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Scrollable history */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : groupedVisits.length === 0 ? (
            <div className="text-center py-16 px-4">
              <FileText className="w-8 h-8 text-muted-foreground/20 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No visit history found</p>
            </div>
          ) : (
            <div className="px-4 py-3">
              {groupedVisits.map((visit, i) => (
                <div key={visit.date} className="relative pl-5 pb-4">
                  {/* Timeline connector */}
                  {i < groupedVisits.length - 1 && (
                    <div className="absolute left-[7px] top-4 bottom-0 w-px bg-border" />
                  )}
                  {/* Timeline dot */}
                  <div className={`absolute left-0 top-1 w-3.5 h-3.5 rounded-full border-2 ${
                    i === 0 ? 'border-primary bg-primary/10' : 'border-border bg-background'
                  } flex items-center justify-center`}>
                    <div className={`w-1.5 h-1.5 rounded-full ${i === 0 ? 'bg-primary' : 'bg-muted-foreground/30'}`} />
                  </div>

                  {/* Date label */}
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-semibold text-muted-foreground">
                      {formatDate(visit.date)}
                    </span>
                    {i === 0 && (
                      <span className="text-2xs bg-primary/10 text-primary px-1.5 py-0.5 rounded-full font-medium">
                        Latest
                      </span>
                    )}
                  </div>

                  {/* Prescriptions */}
                  {can('prescriptions:read') && visit.prescriptions.length > 0 && (
                    <PrescriptionRow
                      prescriptions={visit.prescriptions}
                      onPrint={onPrintPrescription}
                    />
                  )}

                  {/* Invoices */}
                  {can('billing:read') && visit.invoices.map(inv => (
                    <div
                      key={inv.id}
                      className="flex items-center justify-between p-2.5 rounded-lg border border-border bg-background mb-1.5"
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        <Receipt className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs font-medium">
                              ₹{Number(inv.total_amount).toLocaleString('en-IN')}
                            </span>
                            <span className={`text-2xs px-1.5 py-0.5 rounded-full font-medium ${
                              inv.payment_status === 'paid'
                                ? 'bg-success/10 text-success'
                                : inv.payment_status === 'partial'
                                ? 'bg-warning/10 text-warning'
                                : 'bg-destructive/10 text-destructive'
                            }`}>
                              {(inv.payment_status ?? '').replace(/^\w/, (c: string) => c.toUpperCase())}
                            </span>
                          </div>
                          <p className="text-2xs text-muted-foreground">{inv.invoice_number}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {can('billing:write') && (inv.payment_status === 'unpaid' || inv.payment_status === 'partial') && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-6 text-2xs px-2 text-warning border-warning/30 hover:bg-warning/10"
                            onClick={() => onCollectPayment(inv)}
                          >
                            Collect
                          </Button>
                        )}
                        <button
                          onClick={() => onPrintInvoice(inv)}
                          className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                          title="Print invoice"
                        >
                          <Printer className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
